import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Card, Button, Input, Table, Avatar, Spin } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import './TopCreditUsers.scss'

const { Search } = Input

const TopCreditUsers = memo(({
  data,
  loading,
  onExportClick,
  onHistoryClick,
  onSearch,
  searchTerm,
  pagination,
  onPageChange
}) => {
  const tableData = useMemo(() => {
    if (!data?.topUsers) return []

    return data.topUsers.map((user, index) => ({
      key: user.id || index,
      rank: user.rank,
      user: user.name || 'Unknown User',
      avatar: user.avatar || '',
      credits: user.totalCreditsUsed || 0,
    }))
  }, [data, pagination])

  const columns = useMemo(() => [
    {
      title: 'Rank #',
      dataIndex: 'rank',
      key: 'rank',
      width: 100,
      render: rank => {
        const suffix = rank === 1 ? 'st' : rank === 2 ? 'nd' : rank === 3 ? 'rd' : 'th'
        return (
          <span className="rank-cell">
            {rank}
            <span className="rank-suffix">{suffix}</span>
          </span>
        )
      }
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (user, record) => (
        <div className="user-info">
          <Avatar src={record.avatar} className="user-avatar">
            {user?.charAt(0)?.toUpperCase()}
          </Avatar>
          <span className="user-name">{user}</span>
        </div>
      )
    },
    {
      title: 'Total Credits Used',
      dataIndex: 'credits',
      key: 'credits',
      render: credits => credits.toLocaleString()
    }
  ], [])

  return (
    <Card className="top-credit-users-card">
      <div className="top-credit-users-content">
        <div className="section-header">
          <h3 className="section-title">Top Credit Users</h3>
        </div>

        <div className="filters-section">
          <div className="header-actions">
            <Button
              onClick={onExportClick}
            >
              <DownloadOutlined /> Export Report
            </Button>
            {/* <Button
              onClick={onHistoryClick}
            >
              <ClockCircleOutlined /> History
            </Button> */}
            <Search
              placeholder="Search by name..."
              allowClear
              value={searchTerm}
              onChange={e => onSearch(e.target.value)}
              onSearch={value => onSearch(value)}
            />
          </div>
        </div>

        <div className="table-section">
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={{
                ...pagination,
                onChange: onPageChange,
                onShowSizeChange: onPageChange,
                showSizeChanger: false,
                pageSizeOptions: [10, 20, 50, 100]
              }}
              className="users-table"
            />
          </Spin>
        </div>
      </div>
    </Card>
  )
})

TopCreditUsers.propTypes = {
  data: PropTypes.shape({
    topUsers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        avatar: PropTypes.string,
        totalCreditsUsed: PropTypes.number,
        level: PropTypes.string
      })
    )
  }),
  loading: PropTypes.bool,
  onExportClick: PropTypes.func,
  onHistoryClick: PropTypes.func,
  onSearch: PropTypes.func,
  searchTerm: PropTypes.string,
  pagination: PropTypes.object,
  onPageChange: PropTypes.func
}

TopCreditUsers.displayName = 'TopCreditUsers'

export default TopCreditUsers
